/**
 * Handoff from a proposal that just went live to its tradeable market page.
 *
 * On chain the final `back` opens trading in the same transaction; the client
 * learns it from `lifecycle.v0` (proposal status `live`). The page keeps the
 * funded proposal on screen ("Creating market…") while it FULL-prefetches the
 * market route, then navigates onto the prefetched page — no proposal
 * skeleton, no market loading skeleton, no scroll jump.
 */

/**
 * Past the API's one-second hot cache before prefetching, so the market page
 * renders as trading; a cached `backing` row redirects it back to the proposal.
 */
export const LAUNCH_PREFETCH_DELAY_MS = 1_200;
/** Longest the funded proposal waits on the market route's prefetch before navigating anyway. */
export const LAUNCH_PREFETCH_MAX_WAIT_MS = 3_000;

/** A page that opened on an already-live proposal has no hot-cache window to wait out. */
export function launchPrefetchDelayMs(watchedGoLive: boolean): number {
  return watchedGoLive ? LAUNCH_PREFETCH_DELAY_MS : 0;
}

/**
 * Largest `?_rsc=` body that is still only a route tree. A Next 16 full
 * prefetch fetches the route tree (`Next-Router-Segment-Prefetch: /_tree`)
 * when the router has not seen the route, then the dynamic payload the
 * navigation renders from; with the tree cached it fetches the payload alone.
 * Measured on the market route: trees ~275 B, payloads 20–24 KB.
 */
export const ROUTE_TREE_RESPONSE_MAX_BYTES = 2_048;

/** A finished resource request as the browser reports it. */
export interface RouteResourceEntry {
  readonly name: string;
  readonly encodedBodySize: number;
}

/** A finished RSC request of a route prefetch (`?_rsc=` on the route's path). */
export function isRoutePrefetchResponse(resourceUrl: string, routePath: string): boolean {
  if (!URL.canParse(resourceUrl)) return false;
  const url = new URL(resourceUrl);
  return url.searchParams.has('_rsc') && url.pathname.endsWith(routePath);
}

/**
 * The prefetch response that fills the page payload a navigation renders from,
 * whether or not a route-tree response came first. Handing off on the tree
 * alone lands on the market route's loading boundary.
 */
export function isRoutePayloadResponse(entry: RouteResourceEntry, routePath: string): boolean {
  return (
    isRoutePrefetchResponse(entry.name, routePath) &&
    entry.encodedBodySize > ROUTE_TREE_RESPONSE_MAX_BYTES
  );
}

/**
 * The funded total shown while launching. The final back met the threshold on
 * chain, but the proposal row's total only refreshes on a throttled refetch,
 * so the bar would otherwise sit short of 100% until the page leaves.
 */
export function launchDisplayFundedD6(totalFundedD6: string, thresholdD6: string | null): string {
  if (thresholdD6 === null || !/^\d+$/.test(totalFundedD6) || !/^\d+$/.test(thresholdD6)) {
    return totalFundedD6;
  }
  return BigInt(totalFundedD6) >= BigInt(thresholdD6) ? totalFundedD6 : thresholdD6;
}
